import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { toast } from "sonner";

/**
 * Renders the itemized outstanding items for a trip.
 * Shared between the side drawer and the full-page view.
 * Compact by default; each section is expandable.
 */
export function ClientOutstandingDetail({ data, onAction }) {
  const [openKeys, setOpenKeys] = useState(new Set());
  if (!data) return null;
  const o = data.outstanding || {};
  const items = [
    { key: "unpaid_budget", label: "Unpaid budget lines", count: o.unpaid_budget?.count || 0,
      render: () => (
        <ul className="text-[13px] leading-relaxed space-y-1.5 mt-2">
          {(o.unpaid_budget?.items || []).map((l) => (
            <li key={l.id} className="flex justify-between gap-3 border-b border-[color:var(--tcm-navy-15)] pb-1.5">
              <span>
                <span className="font-medium">{l.label}</span>
                {l.supplier && <span className="text-[color:var(--tcm-navy-70)]"> · {l.supplier}</span>}
                <span className="ml-1 tcm-eyebrow text-[9px]" style={{ letterSpacing: "0.12em" }}>{l.status}</span>
              </span>
              <span className="tabular-nums whitespace-nowrap">{l.currency} {Number(l.amount || 0).toFixed(0)}</span>
            </li>
          ))}
        </ul>
      )},
    { key: "missing_receipts", label: "Paid supplier lines without receipts", count: o.missing_receipts?.count || 0,
      render: () => (
        <ul className="text-[13px] space-y-1.5 mt-2">
          {(o.missing_receipts?.items || []).map((l) => (
            <li key={l.id} className="flex justify-between gap-3 border-b border-[color:var(--tcm-navy-15)] pb-1.5">
              <span>{l.label}{l.supplier ? ` · ${l.supplier}` : ""}</span>
              <span className="tabular-nums">{l.currency} {Number(l.amount || 0).toFixed(0)}</span>
            </li>
          ))}
        </ul>
      )},
    { key: "empty_itinerary_days", label: "Itinerary days with no items", count: o.empty_itinerary_days?.count || 0,
      render: () => (
        <p className="text-[13px] mt-2">
          Days: <span className="tabular-nums">{(o.empty_itinerary_days?.days || []).join(", ") || "—"}</span>
        </p>
      )},
    { key: "unread_messages", label: "Unread client messages", count: o.unread_messages?.count || 0,
      render: () => (
        <ul className="text-[13px] space-y-1.5 mt-2">
          {(o.unread_messages?.items || []).map((m) => (
            <li key={m.id} className="border-b border-[color:var(--tcm-navy-15)] pb-1.5">
              <span className="italic">"{m.preview}…"</span>
            </li>
          ))}
        </ul>
      )},
    { key: "missing_docs", label: "Missing key documents", count: o.missing_docs?.count || 0,
      render: () => (
        <p className="text-[13px] mt-2">
          Missing: <span className="uppercase tracking-[0.14em] text-[11px]">{(o.missing_docs?.categories || []).join(" · ") || "—"}</span>
        </p>
      )},
  ];
  const toggle = (k) => {
    const n = new Set(openKeys);
    n.has(k) ? n.delete(k) : n.add(k);
    setOpenKeys(n);
  };
  // Auto-open the first section with items on first render.
  const autoOpenKey = items.find((i) => i.count > 0)?.key;
  const effectiveOpen = openKeys.size === 0 && autoOpenKey ? new Set([autoOpenKey]) : openKeys;

  return (
    <div className="space-y-1">
      {items.map((it) => {
        const hasItems = it.count > 0;
        const isOpen = effectiveOpen.has(it.key);
        return (
          <div key={it.key} className="border-b border-[color:var(--tcm-navy-15)]" data-testid={`outstanding-${it.key}`}>
            <button
              onClick={() => hasItems && toggle(it.key)}
              className={`w-full flex justify-between items-center py-3 text-left ${hasItems ? "cursor-pointer" : "cursor-default opacity-70"}`}>
              <span className="text-[13.5px]">{it.label}</span>
              <span className={`inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase ${hasItems ? "text-[color:var(--tcm-gold-700)]" : "text-[color:var(--tcm-navy-40)]"}`}>
                <span className="font-display text-[18px] tabular-nums">{it.count}</span>
                {hasItems && <span>{isOpen ? "−" : "+"}</span>}
              </span>
            </button>
            {isOpen && hasItems && <div className="pb-4 pl-1">{it.render()}</div>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Fetches and renders the client detail, meant to be reused in drawer + full page.
 */
export function ClientDetailBlock({ tripId }) {
  const { API, authHeaders } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!tripId) return;
    axios
      .get(`${API}/admin/clients/${tripId}/detail`, { headers: authHeaders })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load client"));
  }, [tripId]);

  if (!data) return <p className="p-6 tcm-eyebrow">Loading…</p>;
  const c = data.client || {};
  const staffNames = (data.staff || []).map((s) => s.full_name).join(", ") || "—";
  const segLabel = { vip: "VIP", repeat: "Repeat", first_time: "First-time" }[data.segment] || "—";

  return (
    <div className="space-y-6" data-testid="client-detail-block">
      <div>
        <p className="tcm-eyebrow">{data.bucket === "live" ? "Live now" : data.bucket === "planning" ? "Upcoming" : "Past"}</p>
        <h2 className="font-display text-[32px] leading-[1.05] mt-1">{c.full_name || "Client"}</h2>
        <p className="text-[14px] text-[color:var(--tcm-navy-70)] mt-1">{c.email}</p>
        <span data-testid="detail-segment" className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.14em] uppercase px-2.5 py-1 border border-[color:var(--tcm-gold)]" style={{ color: "var(--tcm-navy)" }}>
          {segLabel}{data.segment_manual && <span className="opacity-70">· pinned</span>}
        </span>
      </div>
      <hr className="tcm-gold-rule w-10" />
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
        <div>
          <dt className="tcm-eyebrow mb-1">Destination</dt>
          <dd className="font-display text-[19px]">{data.destination}</dd>
        </div>
        <div>
          <dt className="tcm-eyebrow mb-1">Dates</dt>
          <dd className="tabular-nums">{data.start_date} – {data.end_date}</dd>
        </div>
        <div>
          <dt className="tcm-eyebrow mb-1">Guests</dt>
          <dd className="tabular-nums">{data.party_size}</dd>
        </div>
        <div>
          <dt className="tcm-eyebrow mb-1">Staff owner</dt>
          <dd>{staffNames}</dd>
        </div>
      </dl>
      <div>
        <div className="flex justify-between items-baseline pb-2 border-b border-[color:var(--tcm-gold)]">
          <p className="text-[11px] tracking-[0.16em] uppercase" style={{ color: "var(--tcm-gold-700)" }}>Outstanding items</p>
          <span className="font-display text-[22px] tabular-nums">{data.outstanding?.total || 0}</span>
        </div>
        <ClientOutstandingDetail data={data} />
      </div>
    </div>
  );
}
